import { $createMarkNode } from "@lexical/mark";
import { $dfs } from "@lexical/utils";
import { diffArrays } from "diff";
import {
  $createTextNode,
  $getRoot,
  $isBlockElementNode,
  $isDecoratorNode,
  $isElementNode,
  $isTextNode,
  $parseSerializedNode,
  type ElementNode,
  type LexicalNode,
  type SerializedEditorState,
  type SerializedElementNode,
  type SerializedLexicalNode,
} from "lexical";

import { ColorHex } from "~/shared/enums";
import { BACKGROUND_ALPHA } from "~/utils/color";

import { getHeadlessEditorWithNodes, loadHeadlessEditorState } from "../utils";

const ADDED_STYLE = `background-color: ${ColorHex.LIME_GREEN}${BACKGROUND_ALPHA}`;
const REMOVED_STYLE = `background-color: ${ColorHex.BRIGHT_RED}${BACKGROUND_ALPHA}; text-decoration: line-through`;

type DiffCharacter = { character: string; format: number; style: string; serialized?: SerializedLexicalNode };
type EarlierBlock = {
  text: string;
  type: string;
  isTextBlock: boolean;
  isContainerBlock: boolean;
  characters: DiffCharacter[];
  children: EarlierBlock[];
  serialized: SerializedLexicalNode;
};

const $getCharacters = (node: ElementNode): DiffCharacter[] =>
  $dfs(node).flatMap(({ node: child }) => {
    if ($isTextNode(child)) {
      return Array.from(child.getTextContent()).map((character) => ({
        character,
        format: child.getFormat(),
        style: child.getStyle(),
      }));
    }
    if ($isDecoratorNode(child)) {
      const serialized = child.exportJSON();
      return [{ character: JSON.stringify(serialized), format: 0, style: "", serialized }];
    }
    return [];
  });

const charactersToText = (characters: DiffCharacter[]) => characters.map(({ character }) => character).join("");

const $getBlockText = (node: LexicalNode): string =>
  $isElementNode(node) ? charactersToText($getCharacters(node)) : node.getTextContent();

const mergeStyles = (style: string, diffStyle: string) => [style, diffStyle].filter(Boolean).join(";");

const $wrapDiffDecoratorNode = (node: LexicalNode, removed: boolean): LexicalNode => {
  const mark = $createMarkNode().append(node);
  return removed ? $createMarkNode().append(mark) : mark;
};

const $replaceDecoratorNodeWithDiffMark = (node: LexicalNode, removed: boolean) => {
  const mark = $createMarkNode();
  node.replace(removed ? $createMarkNode().append(mark) : mark);
  mark.append(node);
};

const $createDiffNode = ({ character, format, style, serialized }: DiffCharacter, diffStyle: string): LexicalNode => {
  if (!serialized) {
    return $createTextNode(character).setFormat(format).setStyle(mergeStyles(style, diffStyle));
  }
  const node = $parseSerializedNode(serialized);
  if (diffStyle === ADDED_STYLE || diffStyle === REMOVED_STYLE) {
    return $wrapDiffDecoratorNode(node, diffStyle === REMOVED_STYLE);
  }
  return node;
};

const $applyDiffStyle = (node: LexicalNode, diffStyle: string) => {
  if ($isDecoratorNode(node)) {
    $replaceDecoratorNodeWithDiffMark(node, diffStyle === REMOVED_STYLE);
    return;
  }
  if ($isElementNode(node)) {
    node.getAllTextNodes().forEach((textNode) => textNode.setStyle(mergeStyles(textNode.getStyle(), diffStyle)));
    $dfs(node).forEach(({ node: child }) => {
      if ($isDecoratorNode(child)) {
        $replaceDecoratorNodeWithDiffMark(child, diffStyle === REMOVED_STYLE);
      }
    });
  }
};

const $hasBlockChild = (node: ElementNode) => node.getChildren().some($isBlockElementNode);

const $isTextBlock = (node: LexicalNode): node is ElementNode => $isBlockElementNode(node) && !$hasBlockChild(node);

const $isContainerBlock = (node: LexicalNode): node is ElementNode => $isBlockElementNode(node) && $hasBlockChild(node);

const $isContainerMatch = (earlierBlock: EarlierBlock, node: LexicalNode): node is ElementNode =>
  $isContainerBlock(node) && earlierBlock.type === node.getType();

const $buildEarlierBlock = (node: LexicalNode, serialized: SerializedLexicalNode): EarlierBlock => {
  const isTextBlock = $isTextBlock(node);
  const isContainerBlock = $isContainerBlock(node);
  return {
    text: $getBlockText(node),
    type: node.getType(),
    isTextBlock,
    isContainerBlock,
    characters: isTextBlock ? $getCharacters(node) : [],
    children: isContainerBlock
      ? node
          .getChildren()
          .map((child, childIndex) =>
            $buildEarlierBlock(child, (serialized as SerializedElementNode).children[childIndex])
          )
      : [],
    serialized,
  };
};

const tokenize = (characters: DiffCharacter[]): DiffCharacter[][] => {
  const tokens: DiffCharacter[][] = [];
  characters.forEach((character) => {
    const last = tokens[tokens.length - 1];
    const isSpace = character.character.trim().length === 0;
    if (last && !character.serialized && !last[0].serialized && (last[0].character.trim().length === 0) === isSpace) {
      last.push(character);
    } else {
      tokens.push([character]);
    }
  });
  return tokens;
};

const $applyWordDiff = (block: ElementNode, earlierCharacters: DiffCharacter[]) => {
  const earlierTokens = tokenize(earlierCharacters);
  const laterTokens = tokenize($getCharacters(block));
  block.clear();
  diffArrays(earlierTokens, laterTokens, {
    comparator: (earlierToken, laterToken) => charactersToText(earlierToken) === charactersToText(laterToken),
  }).forEach((change) => {
    const diffStyle = change.removed ? REMOVED_STYLE : change.added ? ADDED_STYLE : "";
    change.value.flat().forEach((character) => block.append($createDiffNode(character, diffStyle)));
  });
};

const $appendRemovedBlock = (block: EarlierBlock, parent: ElementNode, before: LexicalNode | undefined) => {
  const node = $parseSerializedNode(block.serialized);
  $applyDiffStyle(node, REMOVED_STYLE);
  if (before) {
    before.insertBefore(node);
  } else {
    parent.append(node);
  }
};

const $diffChildren = (parent: ElementNode, earlierChildren: EarlierBlock[]) => {
  const blocks = parent.getChildren().map((node) => ({ text: $getBlockText(node), node }));

  let removed: EarlierBlock[] = [];
  let earlierIndex = 0;
  let index = 0;
  diffArrays(
    earlierChildren.map(({ text }) => text),
    blocks.map(({ text }) => text)
  ).forEach((change) => {
    const count = change.value.length;
    if (change.removed) {
      removed = earlierChildren.slice(earlierIndex, earlierIndex + count);
      earlierIndex += count;
      return;
    }
    const run = blocks.slice(index, index + count);
    if (change.added) {
      for (let pair = 0; pair < Math.max(count, removed.length); pair += 1) {
        const earlierBlock = removed[pair];
        const node = run[pair]?.node;
        if (earlierBlock?.isTextBlock && node && $isTextBlock(node)) {
          $applyWordDiff(node, earlierBlock.characters);
        } else if (earlierBlock?.isContainerBlock && node && $isContainerMatch(earlierBlock, node)) {
          $diffChildren(node, earlierBlock.children);
        } else {
          if (node) {
            $applyDiffStyle(node, ADDED_STYLE);
          }
          if (earlierBlock) {
            $appendRemovedBlock(earlierBlock, parent, blocks[index + count]?.node);
          }
        }
      }
    } else {
      removed.forEach((block) => $appendRemovedBlock(block, parent, run[0]?.node));
      earlierIndex += count;
    }
    removed = [];
    index += count;
  });
  removed.forEach((block) => $appendRemovedBlock(block, parent, undefined));
};

const buildInlineDiffState = (earlier: SerializedEditorState, later: SerializedEditorState): SerializedEditorState => {
  const editor = getHeadlessEditorWithNodes();

  loadHeadlessEditorState(editor, earlier);
  const earlierBlocks: EarlierBlock[] = editor.getEditorState().read(() =>
    $getRoot()
      .getChildren()
      .map((node, index) => $buildEarlierBlock(node, earlier.root.children[index]))
  );

  loadHeadlessEditorState(editor, later);
  editor.update(
    () => {
      $diffChildren($getRoot(), earlierBlocks);
    },
    { discrete: true }
  );

  return editor.getEditorState().toJSON();
};

export default buildInlineDiffState;
